// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useState } from 'react';

import {
  OUT_OF_BOUNDS_KIND,
  type HoleCentreline,
  type HoleLine,
  type HoleShape,
} from '@/features/hole/holeview/holeShapes';

import { parseLineStringPath, parsePolygonRing } from './useCourseAnalysis';

/**
 * A tee, a green, or a turning point — the hole's playing positions, as points.
 *
 * A `turn` row is always somebody's: it is written only by an admin dragging the bend where
 * they want it (see `holeTurnPoints`), never derived from imagery.
 */
export type HoleFeature = {
  id: string;
  holeNumber: number;
  kind: 'tee' | 'turn' | 'green';
  ordinal: number;
  /** The tee's OSM name ("Gul/Vit"). Null for greens and GPS-derived rows. */
  name: string | null;
  lat: number;
  lon: number;
  source: string;
  /** Which imagery provider produced this row, when source is 'satellite'. Null otherwise. */
  sourceProvider: string | null;
  /** 0..1. Filter on this rather than on sampleCount. */
  confidence: number;
  /** Independent contributors behind the estimate, not the number of fixes. */
  sampleCount: number;
  isReviewed: boolean;
  /** pending | accepted | rejected. Rejected rows are not served, so this is never 'rejected' here. */
  reviewStatus: 'pending' | 'accepted' | 'rejected';
  /** The upstream element an imported row came from ("way/123"), when known. */
  externalRef: string | null;
  /** Imagery providers whose latest detections agree something of this kind is here; 0 for rows no analysis produced. */
  agreementCount?: number;
  /**
   * Whether a player is shown this row. False only in an admin's `includeUnserved` read, and
   * absent on anything that is not a published row at all — a detector's raw candidates carry
   * no serving status, and must not read as "hidden from players" for lack of one.
   */
  served?: boolean;
};

type FeatureResponse = Record<string, unknown>;

const num = (row: FeatureResponse, ...keys: string[]): number => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return 0;
};

const str = (row: FeatureResponse, ...keys: string[]): string => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return '';
};

// An older payload has no flag: everything it carried was served.
const servedOf = (row: FeatureResponse): boolean => !(row.served === false || row.Served === false);

// Payload casing varies across the services this passes through, so read both.
const mapFeature = (row: FeatureResponse): HoleFeature | null => {
  const id = str(row, 'id', 'Id');
  const kind = str(row, 'kind', 'Kind');
  if (!id || (kind !== 'tee' && kind !== 'green' && kind !== 'turn')) return null;

  return {
    id,
    holeNumber: num(row, 'holeNumber', 'HoleNumber'),
    kind,
    ordinal: num(row, 'ordinal', 'Ordinal'),
    name: str(row, 'name', 'Name') || null,
    lat: num(row, 'lat', 'Lat'),
    lon: num(row, 'lon', 'Lon'),
    source: str(row, 'source', 'Source') || 'gps',
    sourceProvider: str(row, 'sourceProvider', 'SourceProvider') || null,
    confidence: num(row, 'confidence', 'Confidence'),
    sampleCount: num(row, 'sampleCount', 'SampleCount'),
    isReviewed: row.isReviewed === true || row.IsReviewed === true,
    reviewStatus: ((): HoleFeature['reviewStatus'] => {
      const raw = str(row, 'reviewStatus', 'ReviewStatus');
      if (raw === 'accepted' || raw === 'rejected' || raw === 'pending') return raw;
      // Older payloads carry only the boolean.
      return row.isReviewed === true || row.IsReviewed === true ? 'accepted' : 'pending';
    })(),
    externalRef: str(row, 'externalRef', 'ExternalRef') || null,
    agreementCount: num(row, 'agreementCount', 'AgreementCount'),
    served: servedOf(row),
  };
};

/**
 * A boundary with the provenance the row it came from carries.
 *
 * The drawing surfaces need nothing but the ring, which is why {@link HoleShape} is only
 * that. The admin's mapping browser needs the rest: "a bunker is drawn here" is not a
 * reviewable statement until you can also see that it was guessed from imagery at 60%.
 */
export type HoleShapeFeature = HoleShape & {
  holeNumber: number;
  /** The tee's OSM name, on the rare boundary row that has one. */
  name: string | null;
  source: string;
  /** Which imagery provider produced this row, when source is 'satellite'. Null otherwise. */
  sourceProvider: string | null;
  /** 0..1. */
  confidence: number;
  sampleCount: number;
  /** Imagery providers whose latest detections agree something of this kind is here. */
  agreementCount?: number;
  /** Whether a player is shown this boundary; absent on a candidate, which is not published at all. */
  served?: boolean;
  /**
   * The matcher's verdict on an evaluation polygon — matched · missed · false-positive. Only on
   * shapes whose source is the evaluation layer; what the map colours them by.
   */
  outcome?: string;
};

/**
 * A stored boundary from the same payload.
 *
 * Read alongside the points rather than by a second request: the endpoint returns the whole
 * course either way, and the hole view needs both to draw one hole.
 */
const mapShape = (row: FeatureResponse): HoleShapeFeature | null => {
  const id = str(row, 'id', 'Id');
  const kind = str(row, 'kind', 'Kind');
  const ring = parsePolygonRing(str(row, 'polygonGeoJson', 'PolygonGeoJson') || null);
  if (!id || !kind || !ring) return null;

  return {
    id,
    kind,
    ring,
    holeNumber: num(row, 'holeNumber', 'HoleNumber'),
    name: str(row, 'name', 'Name') || null,
    source: str(row, 'source', 'Source') || 'gps',
    sourceProvider: str(row, 'sourceProvider', 'SourceProvider') || null,
    confidence: num(row, 'confidence', 'Confidence'),
    sampleCount: num(row, 'sampleCount', 'SampleCount'),
    agreementCount: num(row, 'agreementCount', 'AgreementCount'),
    served: servedOf(row),
  };
};

/**
 * A hole's playing line, from the same payload.
 *
 * Its own row kind (`centreline`), stored as a LineString, so it arrives here alongside the
 * boundaries and has to be separated back out. It is what a hazard is measured against on a
 * hole whose fairway and green were never traced (see `shapesNearHole`), and what the hole map
 * draws in place of the tee-green chord (see `playingLineFor`).
 */
const mapCentreline = (row: FeatureResponse): HoleCentreline | null => {
  if (str(row, 'kind', 'Kind') !== 'centreline') return null;
  const path = parseLineStringPath(str(row, 'polygonGeoJson', 'PolygonGeoJson') || null);
  if (!path) return null;
  return {
    holeNumber: num(row, 'holeNumber', 'HoleNumber'),
    path,
    source: str(row, 'source', 'Source') || 'osm',
  };
};

/**
 * A stored line with the provenance the admin's map needs: where the stakes said out of
 * bounds is, and whether it was laid on a mapped edge (`sourceProvider` starts with
 * `stake-snap:`) or is the raw chain of fixes (`stake-chain`).
 */
export type HoleLineFeature = HoleLine & {
  sourceProvider: string | null;
  confidence: number;
  sampleCount: number;
  reviewStatus: 'pending' | 'accepted' | 'rejected';
  served?: boolean;
};

/**
 * An out-of-bounds line, from the same payload. A LineString like the centreline, but a
 * different thing: not where the hole is played, where it stops.
 */
const mapLine = (row: FeatureResponse): HoleLineFeature | null => {
  const id = str(row, 'id', 'Id');
  const kind = str(row, 'kind', 'Kind');
  if (!id || kind !== OUT_OF_BOUNDS_KIND) return null;
  const path = parseLineStringPath(str(row, 'polygonGeoJson', 'PolygonGeoJson') || null);
  if (!path) return null;
  const rawStatus = str(row, 'reviewStatus', 'ReviewStatus');
  return {
    id,
    kind,
    holeNumber: num(row, 'holeNumber', 'HoleNumber'),
    path,
    source: str(row, 'source', 'Source') || 'gps',
    sourceProvider: str(row, 'sourceProvider', 'SourceProvider') || null,
    confidence: num(row, 'confidence', 'Confidence'),
    sampleCount: num(row, 'sampleCount', 'SampleCount'),
    reviewStatus: rawStatus === 'accepted' || rawStatus === 'rejected' ? rawStatus : 'pending',
    served: servedOf(row),
  };
};

export type HoleFeaturesState = {
  features: HoleFeature[];
  /** Every stored boundary on the course, unfiltered by hole or kind. */
  shapes: HoleShapeFeature[];
  /** One playing line per hole, where the course has them — sometimes two; see `playingLineFor`. */
  centrelines: HoleCentreline[];
  /** Every out-of-bounds line on the course, unfiltered by hole. */
  lines: HoleLineFeature[];
  loading: boolean;
  refresh: () => void;
  /** Accepts (marks reviewed) or rejects (deletes) a derived feature. Admin only. */
  review: (featureId: string, accepted: boolean) => Promise<boolean>;
};

export type HoleFeaturesOptions = {
  /**
   * Ask for every stored row, not only what a player is shown: the satellite suggestions under
   * the confidence floor come too, flagged `served: false`. For the admin's map and the review
   * queue — the only places a suggestion nobody is shown can be looked at and accepted.
   */
  includeUnserved?: boolean;
};

export function useHoleFeatures(
  apiBaseUrl: string,
  token: string | null | undefined,
  courseId: string | null,
  options: HoleFeaturesOptions = {}
): HoleFeaturesState {
  const includeUnserved = options.includeUnserved === true;
  const [features, setFeatures] = useState<HoleFeature[]>([]);
  const [shapes, setShapes] = useState<HoleShapeFeature[]>([]);
  const [centrelines, setCentrelines] = useState<HoleCentreline[]>([]);
  const [lines, setLines] = useState<HoleLineFeature[]>([]);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  useEffect(() => {
    if (!token || !courseId) {
      setFeatures([]);
      setShapes([]);
      setCentrelines([]);
      setLines([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    const load = async () => {
      try {
        const response = await fetch(
          `${apiBaseUrl}/api/geodata/courses/${encodeURIComponent(courseId)}/features${includeUnserved ? '?all=true' : ''}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        if (!response.ok) {
          setFeatures([]);
          setShapes([]);
          setCentrelines([]);
      setLines([]);
          return;
        }
        const payload = (await response.json()) as FeatureResponse[];
        if (cancelled || !Array.isArray(payload)) return;
        setFeatures(payload.map(mapFeature).filter((f): f is HoleFeature => f !== null));
        setShapes(payload.map(mapShape).filter((s): s is HoleShapeFeature => s !== null));
        setCentrelines(payload.map(mapCentreline).filter((c): c is HoleCentreline => c !== null));
        setLines(payload.map(mapLine).filter((l): l is HoleLineFeature => l !== null));
      } catch {
        if (!cancelled) {
          setFeatures([]);
          setShapes([]);
          setCentrelines([]);
      setLines([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, courseId, token, reloadKey, includeUnserved]);

  const review = useCallback(
    async (featureId: string, accepted: boolean): Promise<boolean> => {
      if (!token || !courseId) return false;
      try {
        const response = await fetch(
          `${apiBaseUrl}/api/geodata/courses/${encodeURIComponent(courseId)}/features/${encodeURIComponent(featureId)}/review`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ accepted }),
          }
        );
        if (!response.ok) return false;
        refresh();
        return true;
      } catch {
        return false;
      }
    },
    [apiBaseUrl, courseId, token, refresh]
  );

  return { features, shapes, centrelines, lines, loading, refresh, review };
}
